/**
 * BrokerLogo — Real brand logos via Clearbit's free logo CDN with an
 * inline-SVG fallback (used while the image loads, on 404, or when the
 * brand isn't on Clearbit). Each platform card on the LinkBroker page
 * uses this so the marks are immediately recognizable.
 */
import React, { useState } from 'react';

const WRAPPER = (bg, border, size) => ({
  width: size,
  height: size,
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
});

// ── Brand domain → multi-source logo URL chain ──────────────────────────
// 2026-02 update: icon.horse silently returns a 15-byte stub PNG for
// unknown brands which the <img> sees as a "valid" 200 — so we can't rely
// on onError. DuckDuckGo's favicon CDN is the only public service that
// actually returns 404 for missing brands, AND it serves real high-res
// logos for the brokers we care about (match-trade, rithmic, volumetrica,
// closeoption). The direct domain favicon is the second fallback, then
// finally our inline SVG marks.
const BRAND_DOMAIN = {
  mt4:           'metatrader4.com',
  mt5:           'metatrader5.com',
  metaapi:       'metaquotes.net',
  ctrader:       'ctrader.com',
  dxtrade:       'devexperts.com',
  matchtrader:   'match-trade.com',
  tradelocker:   'tradelocker.com',
  tradovate:     'tradovate.com',
  rithmic:       'rithmic.com',
  bybit:         'bybit.com',
  volumetrica:   'volumetricatrading.com',
  closeoption:   'closeoption.com',
  mt5_semi:      'metatrader5.com',
};

const logoSources = (key) => {
  const domain = BRAND_DOMAIN[key];
  if (!domain) return [];
  // Order: DuckDuckGo (most reliable, real 404s) → direct favicon →
  // Google S2 (last resort but follows redirects). icon.horse intentionally
  // removed because of its 15-byte stub problem.
  return [
    `https://icons.duckduckgo.com/ip3/${domain}.ico`,
    `https://${domain}/favicon.ico`,
    `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
  ];
};

// ── Inline-SVG fallback marks (used when Clearbit returns 404) ───────────
const MetaTraderFallback = ({ version = 5 }) => (
  <svg viewBox="0 0 64 64" width="70%" height="70%" aria-label={`MT${version}`}>
    <defs>
      <linearGradient id={`mtgrad${version}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F28E00" />
        <stop offset="100%" stopColor="#CC3300" />
      </linearGradient>
    </defs>
    <rect width="64" height="64" rx="10" fill={`url(#mtgrad${version})`} />
    <path d="M12 48 L12 16 L22 16 L32 36 L42 16 L52 16 L52 48 L43 48 L43 28 L35 44 L29 44 L21 28 L21 48 Z" fill="#fff" />
    <text x="50" y="60" fontSize="12" fontWeight="700" fill="#fff" textAnchor="end" fontFamily="monospace">{version}</text>
  </svg>
);

const CTraderFallback = () => (
  <svg viewBox="0 0 64 64" width="78%" height="78%" aria-label="cTrader">
    <rect width="64" height="64" rx="10" fill="#0B1F2F" />
    <path d="M44 22 C38 16 28 16 22 22 C16 28 16 38 22 44 C28 50 38 50 44 44" stroke="#00D4FF" strokeWidth="6" fill="none" strokeLinecap="round" />
    <circle cx="44" cy="33" r="4" fill="#00D4FF" />
  </svg>
);

const DXtradeFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="DXtrade">
    <rect width="64" height="64" rx="10" fill="#003D7A" />
    <text x="32" y="42" fontSize="22" fontWeight="900" fill="#FFFFFF" textAnchor="middle" fontFamily="sans-serif">DX</text>
  </svg>
);

const MatchTraderFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="MatchTrader">
    <rect width="64" height="64" rx="10" fill="#0E0E0E" />
    <path d="M14 46 L26 18 L32 30 L38 18 L50 46" stroke="#FFC107" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const TradeLockerFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="TradeLocker">
    <rect width="64" height="64" rx="10" fill="#1A1A1A" />
    <circle cx="32" cy="32" r="22" fill="#FF4949" />
    <text x="32" y="40" fontSize="18" fontWeight="900" fill="#fff" textAnchor="middle" fontFamily="sans-serif">TL</text>
  </svg>
);

const TradovateFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="Tradovate">
    <rect width="64" height="64" rx="10" fill="#0B0B0B" />
    <path d="M14 44 L26 26 L34 34 L50 18" stroke="#7AFF00" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
    <path d="M42 18 L50 18 L50 26" stroke="#7AFF00" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const RithmicFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="Rithmic">
    <rect width="64" height="64" rx="10" fill="#1A1A1A" />
    <path d="M20 14 L44 14 L44 34 C44 42 38 48 32 50 C26 48 20 42 20 34 Z" fill="#9B2335" />
    <text x="32" y="38" fontSize="18" fontWeight="900" fill="#fff" textAnchor="middle" fontFamily="serif">R</text>
  </svg>
);

const FundedFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="Funded">
    <rect width="64" height="64" rx="10" fill="#0A1F12" />
    <circle cx="32" cy="32" r="20" fill="none" stroke="#00FF94" strokeWidth="3" />
    <path d="M22 33 L30 41 L44 25" stroke="#00FF94" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MT5AltFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="MT5">
    <rect width="64" height="64" rx="10" fill="#0B1728" />
    <path d="M12 48 L12 16 L22 16 L32 36 L42 16 L52 16 L52 48 L43 48 L43 28 L35 44 L29 44 L21 28 L21 48 Z" fill="#00B4D8" />
  </svg>
);

const BybitFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="Bybit">
    <rect width="64" height="64" rx="10" fill="#15151B" />
    <path d="M44 18 H30 a4 4 0 0 0 -4 4 V42 a4 4 0 0 0 4 4 H44 a8 8 0 0 0 0 -16 a8 8 0 0 0 0 -12 Z" fill="none" stroke="#F7A600" strokeWidth="3.5" />
    <circle cx="40" cy="26" r="2.4" fill="#F7A600" />
    <circle cx="40" cy="38" r="2.4" fill="#F7A600" />
  </svg>
);

const VolumetricaFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="Volumetrica">
    <rect width="64" height="64" rx="10" fill="#031A20" />
    <path d="M12 22 L20 22 L26 44 L32 22 L40 22 L46 44 L52 22" stroke="#00C9D7" strokeWidth="4" fill="none" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const CloseOptionFallback = () => (
  <svg viewBox="0 0 64 64" width="80%" height="80%" aria-label="CloseOption">
    <rect width="64" height="64" rx="10" fill="#0E0E0E" />
    <path d="M14 20 L26 44 M50 20 L38 44" stroke="#FF6B35" strokeWidth="4" fill="none" strokeLinecap="round" />
    <circle cx="32" cy="32" r="6" fill="none" stroke="#FF6B35" strokeWidth="3" />
  </svg>
);

const FALLBACKS = {
  mt4:           ({ size }) => <MetaTraderFallback version={4} />,
  mt5:           ({ size }) => <MetaTraderFallback version={5} />,
  metaapi:       ({ size }) => <MetaTraderFallback version={5} />,
  ctrader:       CTraderFallback,
  dxtrade:       DXtradeFallback,
  matchtrader:   MatchTraderFallback,
  tradelocker:   TradeLockerFallback,
  tradovate:     TradovateFallback,
  rithmic:       RithmicFallback,
  funded_apply:  FundedFallback,
  funded_link:   FundedFallback,
  mt5_semi:      MT5AltFallback,
  bybit:         BybitFallback,
  volumetrica:   VolumetricaFallback,
  closeoption:   CloseOptionFallback,
};

export default function BrokerLogo({ platform, size = 48 }) {
  const key = (platform || '').toLowerCase();
  const sources = logoSources(key);
  const [sourceIdx, setSourceIdx] = useState(0);
  const Fallback = FALLBACKS[key] || FALLBACKS.mt5;
  const currentUrl = sources[sourceIdx];

  return (
    <div style={WRAPPER('#0F0F0F', '#2A2A2A', size)} data-testid={`broker-logo-${key}`}>
      {currentUrl ? (
        <img
          key={currentUrl}
          src={currentUrl}
          alt={`${platform} logo`}
          width={size}
          height={size}
          loading="lazy"
          style={{ width: '88%', height: '88%', objectFit: 'contain', borderRadius: 4 }}
          onError={() => setSourceIdx(i => i + 1)}
        />
      ) : (
        <Fallback size={size} />
      )}
    </div>
  );
}
